import {keepPreviousData} from "@tanstack/react-query";
import {useCustomQuery} from "~/query/queryClient";
import {queryHttp} from "~/query/queryHttp";
import type {PageResponse} from "~/services/ApiTypes";
import type {ContractLifecycle, ContractRow} from "~/pages/contract-explorer/contractExplorerTypes";
import {contractsUrl} from "~/pages/contract-explorer/useContractExplorerQueries";

export type SupplierContractsQueryVariables = {
    /** `undefined` while the databag is still resolving — the query stays disabled until it isn't. */
    databagId: number | undefined;
    /** The entity's `canonical_name`, sent as the list's `search`. */
    supplierName: string;
    /** 1-based, as `useDataGridQueryState` hands it out; turned into `offset` here. */
    page: number;
    pageSize: number;
    /** DRF `ordering` with the API's snake_case keys. */
    ordering: string | undefined;
    lifecycle: ContractLifecycle | undefined;
    /** A document type from the summary's `type_counts` (`master_agreement` …). */
    documentType: string | undefined;
};

/**
 * One page of the contracts that match a supplier — the Contract Explorer's own list endpoint,
 * `GET /data/{databag_id}/contracts/`, searched by the entity's canonical name.
 *
 * `search` matches title, supplier name, label and bundle id server-side, so every row the
 * API returns is shown as-is: a contract that only names the supplier in its title is still in
 * the list. The endpoint pages with DRF's LimitOffset (`offset`/`limit`), not `page`.
 */
export function useSupplierContractsQuery({
                                              databagId,
                                              supplierName,
                                              page,
                                              pageSize,
                                              ordering,
                                              lifecycle,
                                              documentType,
                                          }: SupplierContractsQueryVariables) {
    const url = databagId === undefined ? '' : contractsUrl(databagId);
    const params: Record<string, string | number> = {
        search: supplierName,
        offset: (page - 1) * pageSize,
        limit: pageSize,
    };
    if (ordering) params.ordering = ordering;
    if (lifecycle) params.lifecycle = lifecycle;
    if (documentType) params.document_type = documentType;

    return useCustomQuery({
        queryKey: [url, {params}] as const,
        queryFn: async ({signal}) => {
            const {data} = await queryHttp.get<PageResponse<ContractRow>>(url, {params, signal});
            return {rows: data.results ?? [], rowCount: data.count ?? 0};
        },
        // An empty search would list the whole databag's contracts, not this supplier's.
        enabled: databagId !== undefined && supplierName.trim() !== '',
        // Paging and filtering keep the current page on screen instead of emptying the grid.
        placeholderData: keepPreviousData,
    });
}
